import { Box, Button, HStack, ScrollView, Text } from "components/ui";
import React, { ReactElement, ReactNode, useEffect, useState } from "react";
import { useIsMobile } from "utils/hooks/useIsMobile";

type TabProps = {
	title: string;
	id: string;
	children: ReactNode;
	startIcon?: ReactNode;
	endIcon?: ReactNode;
	bgColor?: string;
};

export function Tab({ title, children }: TabProps) {
	return <Box>{children}</Box>;
}

type TabContainerProps = {
	tabTitleSize?: "tight" | "full";
	children: (ReactElement<TabProps> | boolean)[];
	isFluid?: boolean;
	showSave?: boolean;
	noPadding?: boolean;
	selectedTab?: string;
	onChange?: (tab: string) => void;
};

export function TabContainer({ children, tabTitleSize = "full", showSave, onChange, selectedTab, isFluid, noPadding }: TabContainerProps) {
	const childrenArray = React.Children.toArray(children).filter((child): child is ReactElement<TabProps> => React.isValidElement(child) && Boolean(child));
	const defaultActiveTab = selectedTab || childrenArray[0]?.props.id || "";

	const [activeTab, setActiveTab] = useState(defaultActiveTab);
	const isMobile = useIsMobile();

	// Handle external changes to `selectedTab`
	useEffect(() => {
		if (selectedTab && selectedTab !== activeTab) {
			setActiveTab(selectedTab);
		}
	}, [selectedTab]);

	// Handle changes to children
	useEffect(() => {
		if (!childrenArray.some((child) => child.props.id === activeTab)) {
			setActiveTab(childrenArray[0]?.props.id || "");
		}
	}, [childrenArray, activeTab]);

	const handleTabChange = (tab: string) => {
		setActiveTab(tab);
		onChange?.(tab);
	};

	if (childrenArray.length === 0) {
		return null;
	}

	const renderTabs = () => (
		<>
			{childrenArray.map((child, index) => {
				const isActiveTab = activeTab === child.props.id;
				const textColour = isActiveTab ? "$primarylight" : "$neutral600";
				const fontWeight = isActiveTab ? 700 : 400;
				const borderBottom = isActiveTab ? 2 : 0;

				const tabStyles =
					tabTitleSize === "full"
						? {
								backgroundColor: child.props.bgColor || "$neutral25",
								paddingVertical: 10,
								width: "100%",
								gap: 6,
								// marginRight: 10,
								borderBottomColor: "$primarylight",
								borderBottomWidth: borderBottom,
								flex: 1,
							}
						: {
								backgroundColor: child.props.bgColor || "$neutral25",
								borderBottomColor: "$primarylight",
								borderBottomWidth: borderBottom,
							};

				return (
					<Button key={index} onPress={() => handleTabChange(child.props.id)} backgroundColor="$neutral100" sx={tabStyles}>
						<Box>{child.props.startIcon}</Box>
						<Text sx={{ color: textColour, fontWeight }}>{child.props.title}</Text>
					</Button>
				);
			})}
		</>
	);

	return (
		<>
			<HStack gap={24} alignContent="center" justifyContent={tabTitleSize === "full" ? "space-around" : "flex-start"} w="100%" px={6} role="navigation" sx={{ borderBottomColor: "$neutral100", borderBottomWidth: 1 }}>
				{renderTabs()}
				{showSave && (
					<Button minWidth={80} onPress={() => window.alert("Save")} bg="$primarylight" borderRadius={12}>
						<Text color="$basicwhite" fontWeight="$bold">
							Save
						</Text>
					</Button>
				)}
			</HStack>
			<Box width={isFluid ? "$full" : null} paddingVertical={noPadding ? 0 : 24}>
				{childrenArray.find((child) => child.props.id === activeTab)?.props.children || null}
			</Box>
		</>
	);
}
